import React, { useEffect, useCallback } from "react";
// Type-only import (and the `typeof import("monaco-editor")` below) — erased at build, so it
// does NOT bundle monaco-editor at runtime. The editor itself is lazy-loaded via MonacoLocal.
import type * as Monaco from "monaco-editor";

// Filesystem hook
import { useFilesystem } from "../../../contexts/SandboxHooks";

// Monaco themes hook
import { useMonacoThemes } from "../../../../../../hooks/useMonacoThemes";

// Lazy load local wrapper for Monaco Editor
const MonacoLocal = React.lazy(() => import("./MonacoLocal"));

import S from "./_styles.module.scss";
import EditorSkeleton from "./EditorSkeleton";

const Editor = () => {
  const { monacoTheme, handleEditorMount: onThemeMount } = useMonacoThemes();

  // Access the filesystem context and get the first file to display
  const {
    currentLanguage,
    fileLookup,
    beginEditingFile,
    editor: { currentPath: currentEditorPath, currentContents: currentEditorContents },
    handleEditorChange,
  } = useFilesystem();
  const firstFilePath = fileLookup ? Object.keys(fileLookup)[0] : null;

  const handleMount = useCallback(
    (editor: Monaco.editor.IStandaloneCodeEditor, monaco: typeof import("monaco-editor")) => {
      onThemeMount(editor, monaco);

      const textarea = editor.getDomNode()?.querySelector("textarea");
      if (textarea) {
        textarea.setAttribute("aria-label", "Code editor");
      }
    },
    [onThemeMount]
  );

  useEffect(() => {
    if (firstFilePath && !currentEditorPath) {
      beginEditingFile(firstFilePath);
    }
  }, [firstFilePath, currentEditorPath, beginEditingFile]);

  return (
    <div className={S.container}>
      {currentEditorContents !== null && (
        <React.Suspense fallback={<EditorSkeleton />}>
          <MonacoLocal
            height="100%"
            language={currentLanguage ?? undefined}
            value={currentEditorContents ?? ""}
            onChange={value => handleEditorChange(value ?? "")}
            theme={monacoTheme}
            className={S.editor}
            onMount={handleMount}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default Editor;
